import { useEffect, useState } from "react";
import useWebTracking from "@/hooks/useWebTracking";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { inr } from "@/lib/constants";

export default function DoctorCheckout() {
    useWebTracking();
    const { orderId } = useParams();
    const nav = useNavigate();
    const [order, setOrder] = useState(null);
    const [doctor, setDoctor] = useState(null);
    const [busy, setBusy] = useState(false);
    const [outcome, setOutcome] = useState(null); // null | success | failure

    const load = async () => {
        try {
            const [o, d] = await Promise.all([
                api.get(`/payments/orders/${orderId}`),
                api.get("/me/doctor"),
            ]);
            setOrder(o.data);
            setDoctor(d.data);
        } catch (err) {
            toast.error(errMsg(err, "Order not found"));
            nav("/doctor/packages");
        }
    };

    useEffect(() => {
        load();
    }, [orderId]);

    const simulate = async (want) => {
        setBusy(true);
        try {
            const { data } = await api.post("/payments/mock/simulate", {
                order_id: orderId,
                outcome: want,
            });
            // IMPORTANT: We do NOT trust this response alone — we re-fetch the
            // order from the server so the truth reflects webhook processing.
            await load();
            const fresh = await api.get(`/payments/orders/${orderId}`);
            const finalStatus = fresh.data.status;
            if (finalStatus === "successful") {
                toast.success("Payment successful — invoice generated.", {
                    description: `Invoice #${data?.webhook_result?.invoice_number || "generated"}`,
                });
                setOutcome("success");
            } else if (finalStatus === "failed") {
                toast.error("Payment failed.");
                setOutcome("failure");
            } else {
                toast.info("Awaiting webhook confirmation…");
            }
        } catch (err) {
            toast.error(errMsg(err, "Simulation error"));
        } finally {
            setBusy(false);
        }
    };

    // Lazily load the Cashfree JS SDK once.
    const loadCashfreeSDK = () => new Promise((resolve, reject) => {
        if (window.Cashfree) return resolve(window.Cashfree);
        const s = document.createElement("script");
        s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
        s.async = true;
        s.onload = () => resolve(window.Cashfree);
        s.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
        document.body.appendChild(s);
    });

    const payWithCashfree = async () => {
        if (!order?.payment_session_id) {
            toast.error("Payment session missing — please recreate the order.");
            return;
        }
        setBusy(true);
        try {
            const Cashfree = await loadCashfreeSDK();
            // Iter22: SDK mode driven by the server — cannot mismatch env.
            const mode = order.sdk_mode === "production" ? "production" : "sandbox";
            const cf = Cashfree({ mode });
            const result = await cf.checkout({
                paymentSessionId: order.payment_session_id,
                redirectTarget: "_self",   // full-page redirect on completion
            });
            // Cashfree returns here only on error paths; success flows via
            // the return_url. Either way, re-fetch the truth (webhook).
            if (result?.error) {
                toast.error(result.error.message || "Payment cancelled");
            }
            await load();
        } catch (err) {
            toast.error(errMsg(err, "Cashfree checkout failed"));
        } finally {
            setBusy(false);
        }
    };

    const isMock = order?.gateway === "mock";

    return (
        <DoctorShell doctor={doctor}>
            <div className="max-w-3xl animate-fade-up">
                {/* Test mode banner — MANDATORY per spec */}
                <div
                    className="ddi-card p-4 flex items-start gap-3 border-[#D97706]/50 bg-[#D97706]/10 mb-6"
                    data-testid="mock-checkout-banner"
                >
                    <ShieldAlert size={20} className="text-[#D97706] mt-0.5" />
                    <div className="text-sm text-[#17212B]/80">
                        <div className="font-bold text-[#D97706] uppercase tracking-widest text-[11px]">
                            TEST MODE — Mock Gateway
                        </div>
                        <div className="mt-1">
                            No real card is charged. A signed webhook is fired to
                            our server on both outcomes — the server (never the
                            browser) is the source of truth for payment status.
                        </div>
                    </div>
                </div>

                <div className="ddi-card p-8" data-testid="mock-checkout-panel">
                    <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                        Checkout
                    </div>
                    <h1 className="mt-1 text-2xl font-bold text-[#123B5D]">
                        {order?.package_name}
                        <span className="text-sm font-medium text-[#17212B]/60 ml-2 capitalize">
                            · {order?.installment} installment
                        </span>
                    </h1>

                    <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                        <div className="text-[#17212B]/60">Order ID</div>
                        <div className="text-right font-mono text-[#17212B]/80">
                            {order?.id}
                        </div>
                        <div className="text-[#17212B]/60">Gateway</div>
                        <div className="text-right font-mono text-[#17212B]/80 capitalize">
                            {order?.gateway}
                        </div>
                        <div className="text-[#17212B]/60">Amount</div>
                        <div className="text-right text-[#17212B]">
                            {inr(order?.amount_inr)}
                        </div>
                        <div className="text-[#17212B]/60">GST (18%)</div>
                        <div className="text-right text-[#17212B]">
                            {inr(order?.tax_inr)}
                        </div>
                        <div className="text-[#17212B]/60 border-t border-[#123B5D]/10 pt-3 font-semibold">
                            Total due
                        </div>
                        <div className="text-right text-2xl font-bold text-[#123B5D] border-t border-[#123B5D]/10 pt-3">
                            {inr(order?.total_inr)}
                        </div>
                    </div>

                    <div className="mt-8">
                        {order?.status === "successful" ? (
                            <div className="ddi-card p-4 bg-green-50 border-[#15803D]/30 text-[#15803D] flex items-center gap-2" data-testid="mock-checkout-result-success">
                                <CheckCircle2 size={18} /> Payment successful — see Invoices.
                            </div>
                        ) : order?.status === "failed" ? (
                            <div className="ddi-card p-4 bg-red-50 border-[#DC2626]/30 text-[#DC2626] flex items-center gap-2" data-testid="mock-checkout-result-failed">
                                <XCircle size={18} /> Payment failed.
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-3">
                                {isMock ? (
                                    <>
                                        <button
                                            className="ddi-teal-btn flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                                            disabled={busy}
                                            onClick={() => simulate("success")}
                                            data-testid="mock-checkout-success-btn"
                                        >
                                            <CheckCircle2 size={16} />
                                            {busy && outcome !== "failure" ? "Processing…" : "Simulate Success"}
                                        </button>
                                        <button
                                            className="flex-1 rounded-full px-5 py-3 font-semibold border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                                            disabled={busy}
                                            onClick={() => simulate("failure")}
                                            data-testid="mock-checkout-failure-btn"
                                        >
                                            <XCircle size={16} />
                                            {busy && outcome !== "success" ? "Processing…" : "Simulate Failure"}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        className="ddi-teal-btn flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                                        disabled={busy || !order?.payment_session_id}
                                        onClick={payWithCashfree}
                                        data-testid="cashfree-checkout-btn"
                                    >
                                        <CheckCircle2 size={16} />
                                        {busy ? "Opening Cashfree…" : `Pay ${inr(order?.total_inr)} via Cashfree`}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mt-6 text-xs text-[#17212B]/60 flex items-start gap-2">
                        <AlertTriangle size={14} className="text-[#D97706] mt-0.5" />
                        <span>
                            Per platform policy, the frontend never marks a
                            payment successful on its own. This screen
                            re-fetches order status from the server after each
                            simulation — the webhook is the source of truth.
                        </span>
                    </div>
                </div>

                {order?.status === "successful" ? (
                    <div className="mt-6 flex gap-3">
                        <button
                            className="ddi-outline-btn text-sm"
                            onClick={() => nav("/doctor/invoices")}
                            data-testid="mock-checkout-goto-invoices"
                        >
                            View invoice
                        </button>
                        <button
                            className="ddi-outline-btn text-sm"
                            onClick={() => nav("/doctor")}
                            data-testid="mock-checkout-goto-dashboard"
                        >
                            Back to dashboard
                        </button>
                    </div>
                ) : null}
            </div>
        </DoctorShell>
    );
}
